import { acceptHMRUpdate, defineStore } from 'pinia'

import { ROLE_STATUS, THRESHOLD_TYPES } from '@/settings/constants'
import { ThresholdTypes } from '@/types/api'

type State = {
  bioVTAdmin: boolean
  companyAdmin: boolean
  departmentMember: boolean
  allowedSubscriptionTypes: ThresholdTypes[]
}

type Getters = {
  hasAtLeastOneRole: (state: State) => boolean
  isUserSuperAdmin: (state: State) => boolean
  isUserAdmin: (state: State) => boolean
  isQuantityThresholdAllowed: (state: State) => boolean
  isSubscriptionThresholdAllowed: (state: State) => boolean
  isOnDemandThresholdAllowed: (state: State) => boolean
}

type Actions = {
  setRoles: (params: {
    itsSuperAdmin: boolean
    itsRole: string
    allowedSubscriptionTypes: ThresholdTypes[]
  }) => Promise<void>
  resetRoles: () => void
}

export const useRolesStore = defineStore<string, State, Getters, Actions>('roles', {
  state: () => ({
    bioVTAdmin: false,
    companyAdmin: false,
    departmentMember: false,
    allowedSubscriptionTypes: [],
  }),

  getters: {
    hasAtLeastOneRole: (state) => state.companyAdmin || state.bioVTAdmin || state.departmentMember,
    isUserSuperAdmin: (state) => state.bioVTAdmin,
    isUserAdmin: (state) => state.bioVTAdmin || state.companyAdmin,
    isQuantityThresholdAllowed: (state) =>
      state.bioVTAdmin || state.allowedSubscriptionTypes.includes(THRESHOLD_TYPES.qty),
    isSubscriptionThresholdAllowed: (state) =>
      state.bioVTAdmin || state.allowedSubscriptionTypes.includes(THRESHOLD_TYPES.subscription),
    isOnDemandThresholdAllowed: (state) =>
      state.bioVTAdmin || state.allowedSubscriptionTypes.includes(THRESHOLD_TYPES.ondemand),
  },

  actions: {
    async setRoles({ itsSuperAdmin, itsRole, allowedSubscriptionTypes }) {
      await this.$reset()
      this.bioVTAdmin = itsSuperAdmin || false
      this.companyAdmin = itsRole === ROLE_STATUS.COMPANY_ADMIN
      this.departmentMember = itsRole === ROLE_STATUS.DEPARTMENT_MEMBER
      this.allowedSubscriptionTypes = allowedSubscriptionTypes
    },

    resetRoles() {
      this.$reset()
    },
  },
  persist: true,
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRolesStore, import.meta.hot))
}
